import {FC, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {Dropdown, Modal} from 'react-bootstrap'
import {TermsDocument, TermsStatusReason} from '../core/_models'
import {TermsChanges, TermsDocumentView} from './TermsDocumentView'
import '../Terms.scss'

// How close to the bottom counts as "read to the end" (rounding, zoom, a last line's margin).
const END_TOLERANCE_PX = 32

export type TermsOtherCompany = {id: number; name: string}

type Props = {
  document: TermsDocument
  reason?: TermsStatusReason | null
  /** Only the user's own acceptance is missing (version 2 on): nothing is said about a business. */
  personalOnly?: boolean
  companyName?: string | null
  otherCompanies: TermsOtherCompany[]
  submitting: boolean
  switching: boolean
  error?: string
  onAccept: () => void
  onLogout: () => void
  onSwitchCompany: (companyId: number) => void
}

/**
 * The window a business's user sees on entering the panel while the business has not accepted
 * the terms in force. It cannot be dismissed: the user accepts, signs out, or (with more than one
 * business) opens another business. «خواندم و می‌پذیرم» opens up once the text has been scrolled
 * to its end.
 */
export const TermsAcceptanceModal: FC<Props> = ({
  document,
  reason,
  personalOnly = false,
  companyName,
  otherCompanies,
  submitting,
  switching,
  error,
  onAccept,
  onLogout,
  onSwitchCompany,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const [readToEnd, setReadToEnd] = useState(false)
  const busy = submitting || switching
  const business = companyName?.trim() || 'کسب‌وکار انتخاب‌شده'
  const updated = personalOnly || reason === 'updated'

  const checkEnd = useCallback(() => {
    const body = bodyRef.current
    if (!body) return
    if (body.scrollHeight - body.scrollTop - body.clientHeight <= END_TOLERANCE_PX)
      setReadToEnd(true)
  }, [])

  // A new text (a version published while this one was open) has to be read again from the top.
  useLayoutEffect(() => {
    setReadToEnd(false)
    if (bodyRef.current) bodyRef.current.scrollTop = 0
    checkEnd()
  }, [document.contentHash, checkEnd])

  // A text short enough to fit (a tall screen, a zoomed-out page) needs no scrolling at all.
  useEffect(() => {
    window.addEventListener('resize', checkEnd)
    return () => window.removeEventListener('resize', checkEnd)
  }, [checkEnd])

  return (
    <Modal
      show
      backdrop='static'
      keyboard={false}
      centered
      size='lg'
      fullscreen='sm-down'
      scrollable
      onEntered={checkEnd}
      dialogClassName='mp-terms-dialog'
      aria-labelledby='mp-terms-accept-title'
      aria-describedby='mp-terms-accept-lead'
      data-testid='terms-acceptance-modal'
    >
      <Modal.Header className='mp-terms-dialog__header'>
        <span className='mp-terms-dialog__icon' aria-hidden='true'>
          <i className='las la-file-contract' />
        </span>
        <div>
          <Modal.Title as='h2' id='mp-terms-accept-title'>
            {updated ? 'قوانین و مقررات به‌روزرسانی شد' : 'پذیرش قوانین و مقررات'}
          </Modal.Title>
          {!personalOnly && (
            <p className='mp-terms-dialog__business'>
              کسب‌وکار: <bdi>{business}</bdi>
            </p>
          )}
        </div>
      </Modal.Header>

      <Modal.Body
        ref={bodyRef}
        onScroll={checkEnd}
        tabIndex={0}
        role='region'
        aria-label='متن قوانین و مقررات'
        className='mp-terms-dialog__body'
      >
        <p id='mp-terms-accept-lead' className='mp-terms-dialog__lead'>
          {personalOnly ? (
            <>
              نسخه‌ی تازه‌ی قوانین را هر کاربر باید شخصاً بخواند و بپذیرد. پذیرش شما به نام خودتان
              ثبت می‌شود و برای همه‌ی کسب‌وکارهایتان یک بار کافی است.
            </>
          ) : (
            <>
              {updated
                ? 'متن قوانین و مقررات تغییر کرده است. برای ادامه کار با پنل، نسخه تازه را تا انتها بخوانید و آن را بپذیرید.'
                : 'برای ادامه کار با پنل، قوانین و مقررات استفاده از خدمات را تا انتها بخوانید و آن را بپذیرید.'}{' '}
              پذیرش شما به نام خودتان و کسب‌وکار «<bdi>{business}</bdi>» ثبت می‌شود و برای هر نسخه
              یک بار کافی است.
            </>
          )}
        </p>
        <TermsChanges document={document} />
        <TermsDocumentView document={document} />
        <p className='mp-terms-dialog__end' aria-hidden='true'>
          پایان متن
        </p>
      </Modal.Body>

      <Modal.Footer className='mp-terms-dialog__footer'>
        {error && (
          <div className='alert alert-danger d-flex align-items-start w-100 mb-0' role='alert'>
            <i className='las la-exclamation-circle fs-2 me-2' aria-hidden='true' />
            <span>{error}</span>
          </div>
        )}
        <p
          id='mp-terms-accept-hint'
          className={readToEnd ? 'mp-terms-dialog__hint is-done' : 'mp-terms-dialog__hint'}
          aria-live='polite'
        >
          <i
            className={readToEnd ? 'las la-check-circle' : 'las la-arrow-down'}
            aria-hidden='true'
          />
          {readToEnd
            ? 'متن تا انتها خوانده شد.'
            : 'برای فعال شدن دکمه پذیرش، متن را تا انتها بخوانید.'}
        </p>
        <div className='mp-terms-dialog__actions'>
          <button type='button' className='btn btn-light' onClick={onLogout} disabled={busy}>
            <i className='las la-sign-out-alt fs-4 me-1' aria-hidden='true' />
            خروج از حساب
          </button>
          {otherCompanies.length > 0 && (
            <Dropdown drop='up'>
              <Dropdown.Toggle variant='light' disabled={busy} id='mp-terms-other-company'>
                {switching ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
                    در حال ورود…
                  </>
                ) : (
                  'کسب‌وکار دیگر'
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu className='mp-terms-dialog__companies'>
                {otherCompanies.map((company) => (
                  <Dropdown.Item
                    key={company.id}
                    as='button'
                    type='button'
                    onClick={() => onSwitchCompany(company.id)}
                  >
                    <bdi>{company.name}</bdi>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
          <button
            type='button'
            className='btn btn-primary mp-terms-dialog__accept'
            onClick={onAccept}
            disabled={!readToEnd || busy}
            aria-describedby='mp-terms-accept-hint'
          >
            {submitting ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
                در حال ثبت…
              </>
            ) : (
              <>
                <i className='las la-check fs-4 me-1' aria-hidden='true' />
                خواندم و می‌پذیرم
              </>
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
